import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { unwrapListEnvelope } from '../utils/apiNormalize';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function normalizeStudentsResponse(data) {
  const list = unwrapListEnvelope(data, 'users');

  if (!Array.isArray(list)) return [];
  return list
    .filter((user) => (user?.role || '').toLowerCase() === 'student')
    .map((s) => {
      const id = s?.id != null ? String(s.id) : s?.user_id != null ? String(s.user_id) : '';
      const fullName = s?.full_name ?? s?.name ?? '';
      const email = s?.email_id ?? s?.email ?? '';
      const phone = s?.phone_number ?? s?.phone ?? '';
      const course = s?.course ?? '';
      const hasPurchased = Boolean(
        s?.has_purchased ??
          s?.is_purchased ??
          s?.purchased ??
          s?.is_paid ??
          s?.payment_done ??
          (typeof s?.purchase_status === 'string'
            ? s.purchase_status.toLowerCase() === 'purchased' ||
              s.purchase_status.toLowerCase() === 'paid' ||
              s.purchase_status.toLowerCase() === 'yes'
            : false)
      );
      const createdAt = s?.date ?? s?.created_at ?? s?.createdAt ?? '';

      return {
        id,
        fullName: String(fullName),
        email: String(email),
        phone: String(phone),
        course: String(course),
        hasPurchased: Boolean(hasPurchased),
        createdAt: String(createdAt),
      };
    });
}

function Students() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadStudents() {
      setLoading(true);
      setError('');
      try {
        const res = await apiClient.get('/api/users/');

        if (cancelled) return;

        const studentsList = normalizeStudentsResponse(res.data);
        setStudents(studentsList);
      } catch (e) {
        if (cancelled) return;

        if (e?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }

        let errorMsg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          'Unable to load students. Please try again.';

        if (e?.response?.status === 500) {
          const errorData = e?.response?.data;
          if (errorData) {
            if (typeof errorData === 'string') {
              errorMsg = `Server error: ${errorData}`;
            } else if (errorData.detail) {
              errorMsg = `Server error: ${errorData.detail}`;
            } else if (errorData.message) {
              errorMsg = `Server error: ${errorData.message}`;
            } else {
              errorMsg =
                'Server error: The server encountered an error while loading students. Please check the server logs.';
            }
          } else {
            errorMsg =
              'Server error: The server encountered an error while loading students. Please check the server logs.';
          }
        } else if (!e?.response) {
          const apiUrl = apiClient.defaults.baseURL || 'https://edupravahaa-tech-admin.com';
          if (e?.message?.includes('Network Error') || e?.message?.includes('ERR_NETWORK')) {
            errorMsg = `Network error: Unable to connect to ${apiUrl}. Please check if the API server is running.`;
          }
        }

        setError(errorMsg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const filteredStudents = useMemo(() => {
    const term = search.toLowerCase().trim();
    return students.filter((student) => {
      if (!term) return true;
      return (
        student.fullName.toLowerCase().includes(term) ||
        student.email.toLowerCase().includes(term) ||
        student.phone.toLowerCase().includes(term)
      );
    });
  }, [students, search]);

  const handleEdit = (studentId) => {
    navigate(`/students/edit/${studentId}`);
  };

  const handleDelete = async (studentId, studentName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${studentName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(studentId);
    try {
      await apiClient.delete(`/api/users/${studentId}/`);
      setStudents((prev) => prev.filter((s) => s.id !== studentId));
      setError('');
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Unable to delete student. Please try again.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageTemplate
      title="Student Management"
      subtitle="View and manage student accounts and enrollments"
    >
      <div className="teachers-page">
        {error ? (
          <div className="form-error" style={{ marginBottom: '16px' }}>
            {error}
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginLeft: '12px',
                padding: '4px 12px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        <div className="module-actions">
          <input
            type="search"
            placeholder="Search by name, email, or phone..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/students/add')}
          >
            Add Student
          </button>
        </div>

        <div className="teachers-table-wrapper" role="region" aria-label="Students table">
          <table className="teachers-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th className="table-th-center">Purchase Status</th>
                <th>Created At</th>
                <th className="actions-cell table-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="teachers-empty teachers-empty--loading">
                    Loading students…
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="teachers-empty">
                    {search
                      ? 'No students match your search.'
                      : 'No students found. Add your first student to get started.'}
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id || student.email}>
                    <td className="table-cell-muted">{student.id || '—'}</td>
                    <td className="table-cell-strong">{student.fullName}</td>
                    <td>{student.email}</td>
                    <td>{student.phone || '—'}</td>
                    <td className="table-td-center">
                      <span
                        className={`batch-status-pill ${
                          student.hasPurchased
                            ? 'batch-status-active'
                            : 'batch-status-upcoming'
                        }`}
                      >
                        {student.hasPurchased ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      {student.createdAt
                        ? new Date(student.createdAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="actions-cell table-td-center">
                      <div className="table-actions">
                        <button
                          type="button"
                          className="table-btn table-btn--primary"
                          onClick={() => handleEdit(student.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="table-btn table-btn--danger"
                          onClick={() =>
                            handleDelete(student.id, student.fullName || 'Student')
                          }
                          disabled={deletingId === student.id}
                        >
                          {deletingId === student.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageTemplate>
  );
}

export default Students;
